import { Navigate, Route } from 'react-router-dom';
import catalogPluginAlpha from '@backstage/plugin-catalog/alpha';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import { CatalogImportPage } from '@backstage/plugin-catalog-import';
import { ScaffolderPage } from '@backstage/plugin-scaffolder';
import { GatedScaffolderTaskPage } from './scaffolder/GatedScaffolderTaskPage';
import { SanitizedScaffolderReviewStep } from './scaffolder/SanitizedScaffolderReviewStep';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { AkeylessPathPickerFieldExtension } from './scaffolder/AkeylessPathPicker';
import { GitLabProjectPickerFieldExtension } from './scaffolder/GitLabProjectPicker';
import { GitLabUsernameConditionalFieldExtension } from './scaffolder/GitLabUsernameConditional';
import { IdentityEmailFieldExtension } from './scaffolder/IdentityEmail';
import { SelectFieldFromApiExtension } from '@roadiehq/plugin-scaffolder-frontend-module-http-request-field';
import { SearchPage } from '@backstage/plugin-search';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import {
  AlertDisplay,
} from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import { convertLegacyAppOptions, convertLegacyAppRoot } from '@backstage/core-compat-api';
import { CustomSignInPage } from './components/SignInPage/CustomSignInPage';
import { ironPhoenixOAuthDialogModule } from './ironPhoenixOAuthDialogModule';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';
import { UnifiedThemeProvider } from '@backstage/theme';
import { ironPhoenixTheme, ironPhoenixDarkTheme } from './theme/ironPhoenix';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequestsPage } from './components/Requests/RequestsPage';
import { TicketLogsPage } from './components/Requests/TicketLogsPage';
import { RequestDetailPage } from './components/Requests/RequestDetailPage';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { catalogPageOverrideModule } from './catalogPageOverride';
import devtoolsPlugin from '@backstage/plugin-devtools/alpha';
import { techdocsAppFeature } from './techdocsReaderOverride';
import { SystemInfoPage } from './components/systemInfo/SystemInfoPage';
import { MyGroupsPage } from './components/myGroups/MyGroupsPage';

const convertedOptionsModule = convertLegacyAppOptions({
  apis,
  themes: [
    {
      id: 'iron-phoenix',
      title: 'Iron Phoenix',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={ironPhoenixTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'iron-phoenix-dark',
      title: 'Iron Phoenix (Dark)',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={ironPhoenixDarkTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  components: {
    SignInPage: props => <CustomSignInPage {...props} />,
  },
});

/** Landing: Software Templates (Create) with kind=template and user=all — not catalog home. */
const CREATE_TEMPLATES_LANDING = {
  pathname: '/create',
  search: '?filters[kind]=template&filters[user]=all',
} as const;

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to={CREATE_TEMPLATES_LANDING} replace />} />
    {/* Catalog index and entity routes are provided by catalog plugin (alpha) + catalogPageOverrideModule so catalogIndex and catalogEntity route refs stay bound. */}
    {/* /docs + reader: TechDocs alpha plugin + techdocsReaderOverride (docRoot route ref for EntityListDocsTable). */}
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{
            TaskPageComponent: GatedScaffolderTaskPage,
            ReviewStepComponent: SanitizedScaffolderReviewStep,
          }}
          headerOptions={{
            pageTitleOverride: 'Submit new Request',
            title: 'Submit new Request',
            subtitle: 'Request Templates',
          }}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <GitLabProjectPickerFieldExtension />
        <AkeylessPathPickerFieldExtension />
        <GitLabUsernameConditionalFieldExtension />
        <IdentityEmailFieldExtension />
        <SelectFieldFromApiExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    {/* Not admin-only: "View graph" on entity overview links here for all users; AdminOnlyRoute redirected others to /catalog (looked like wrong page). */}
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/system-info" element={<SystemInfoPage />} />
    <Route path="/my-groups" element={<MyGroupsPage />} />
    <Route path="/requests" element={<RequestsPage />} />
    <Route path="/requests/ticket-logs" element={<TicketLogsPage />} />
    <Route path="/requests/:requestId" element={<RequestDetailPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

const convertedRootFeatures = convertLegacyAppRoot(
  <>
    <AlertDisplay />
    {process.env.NODE_ENV === 'production' && <SignalsDisplay />}
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);

const app = createApp({
  features: [
    convertedOptionsModule,
    catalogPluginAlpha,
    catalogPageOverrideModule,
    apiDocsPlugin, // registers /api-docs in the extension tree (legacy FlatRoutes <ApiExplorerPage /> breaks routeRef resolution)
    techdocsAppFeature, // binds techdocs.docRoot / reader refs; reader overridden for Iron Phoenix
    devtoolsPlugin, // registers /devtools + DevToolsApi (sidebar link is admin-gated in Root)
    ...convertedRootFeatures,
    ironPhoenixOAuthDialogModule,
  ],
});

export default app.createRoot();
